import classNames from 'classnames'
import { FieldArray } from 'formik'
import { isEmpty } from 'lodash'
import React from 'react'
import { useMutation } from 'react-query'
import { Link } from 'react-router-dom'
import { FormActionBar, FormContainer } from 'scenes/Dashboard/components-next/Forms'
import { FormProps } from 'utils-next/forms'

import { Button, InputLabel, Portal } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import TextLink from '@mui/material/Link'

import { MAX_YEAR, MIN_YEAR, YearPicker } from '../../../../../../../../components/YearPicker'
import { TemporalEntityId } from '../../../../../../../../entities-next/common'
import { MutationStatus } from '../../../../../../../../utils-next/api'
import { FastField, Field } from '../../../../../../../../utils-next/forms'
import {
  HtmlEditorField,
  NumberField,
  SwitchField,
  TextField,
} from '../../../../../../components-next/Forms/Controllers'
import { FormSection } from '../../../../../../components-next/Sections'
import { calculateDateFieldValue } from '../../../../../SearchEngine/components/SearchEngineBar/SearchEngineBar.utils'
import { CriteriaLocationSelect } from '../../../../../UsersManager/scenes/UserDetailsView/components/UserDataForm/components/CriteriaLocationSelect.component'
import { OpsLeadSelect } from '../../../../../UsersManager/scenes/UserDetailsView/components/UserDataForm/components/OpsLeadSelect.component'
import { StageSelect } from '../../../../../UsersManager/scenes/UserDetailsView/components/UserDataForm/components/StageSelect.component'
import { useCorporateDataFormStyles } from '../../../../../UsersManager/scenes/UserDetailsView/components/UserDataForm/CorporateDataForm.styles'
import { getApiSearchCandidatesHandlers } from '../../scenes/SearchCandidatesList/SearchCandidates.api'
import { getScoutingCampaignSearchCandidatesViewPath } from '../../ScoutingCampaignDetailsView.routes'
import { FoundedAtPicker } from './components/FoundedAtPicker.component'
import { HoursBetweenDeals } from './components/HoursBetweenDeals.component'
import { ScoutingCampaignStatusSelect } from './components/ScoutingCampaignStatusSelect.component'
import { copyTextToClipboard } from './ScoutingCampaignDataForm.component'
import { ScoutingCampaignFormData } from './ScoutingCampaignDataForm.data'
import { generateDefaultScoutingCampaignParams } from './ScoutingCampaignUtils'

const isValidUrl = (str: string) => {
  const expression = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()!@:%_+.~#?&//=]*)/gi
  const regex = new RegExp(expression)

  return str.match(regex)
}

const handleCopyClick = (term: string) => {
  copyTextToClipboard(term)
}
const googleIt = (term: string) => `https://www.google.com/search?q=${term}`

export type UserDataFormContentProps = FormProps<ScoutingCampaignFormData> & {
  actionBarRef: React.MutableRefObject<any>
}

export function ScoutingCampaignDataFormContent({
  dirty,
  isSubmitting,
  handleSubmit,
  actionBarRef,
  values,
  setValues,
  errors,
  setFieldValue,
}: UserDataFormContentProps) {
  const { classes } = useCorporateDataFormStyles()
  const sampleCompanies = values.sampleCompanies
    ?.split(',')
    .filter((s) => s.length > 0)
    .map((s) => s.trim())

  function onUpdateCriteria() {
    const defaultScoutingCampaign = generateDefaultScoutingCampaignParams({
      organizationStages: values.organizationStages !== '' ? values.organizationStages : undefined,
      organizationFoundedAt: values.organizationFoundedAt !== '' ? values.organizationFoundedAt : undefined,
      includeCompaniesWithoutMaturityInformation:
        values.includeCompaniesWithoutMaturityInformation !== ''
          ? Boolean(values.includeCompaniesWithoutMaturityInformation)
          : undefined,
      organizationHeadquarters: values.organizationHeadquarters !== '' ? values.organizationHeadquarters : undefined,
      negativeOrganizationHeadquarters:
        values.negativeOrganizationHeadquarters !== '' ? values.negativeOrganizationHeadquarters : undefined,
    })
    const updatedScoutingCampaignParam = {
      ...values,
      ...defaultScoutingCampaign,
    }
    setValues(updatedScoutingCampaignParam, true)
  }

  const { internalMinimumYear, internalMaximumYear } = values

  const { apiGenerateSearchCandidatesForSearchFunction } = getApiSearchCandidatesHandlers()
  const { status, mutate: handleSearchCandidatesGeneration } = useMutation(() =>
    apiGenerateSearchCandidatesForSearchFunction(Number(values.id))
  )

  const success = status === MutationStatus.success
  const loading = status === MutationStatus.loading

  return (
    <FormContainer disabled={isSubmitting}>
      <>
        <FormSection title="SC description">
          <div className={classes.formContainer}>
            <FastField
              component={TextField}
              label="Space name"
              name="name"
              className={classes.field}
              fullWidth
              required
              autoFocus
            />
            <div className={classes.formContainer}>
              <FastField type="checkbox" component={SwitchField} label="Editable" name="editable" />
              <FastField
                type="checkbox"
                component={SwitchField}
                label="Counts towards package"
                name="packageCountable"
                className={classes.radarScouting}
              />
              <FastField
                type="checkbox"
                component={SwitchField}
                label="Radar Scouting"
                name="radarScouting"
                className={classes.radarScouting}
              />
            </div>
          </div>
          <div className={classes.formContainer}>
            <FastField
              component={ScoutingCampaignStatusSelect}
              label="Space status"
              name="status"
              className={classes.field}
              required
              fullWidth
            />
          </div>
          <div className={classes.formContainer}>
            <FastField
              component={TextField}
              label="Description"
              name="description"
              className={classes.field}
              required
              fullWidth
              multiline
            />
          </div>
          <div className={classes.formContainer}>
            <FastField
              component={TextField}
              label="Positive keywords"
              name="positiveKeywords"
              className={classes.field}
              fullWidth
              multiline
            />
            <FastField
              component={TextField}
              label="Negative keywords"
              name="negativeKeywords"
              className={classes.field}
              fullWidth
              multiline
            />
          </div>
        </FormSection>
        <FormSection title="Sample Companies">
          <div>
            <FieldArray
              name="sampleCompaniesText"
              render={() => {
                return sampleCompanies.length > 0 ? (
                  <div className={classes.linkWrapper}>
                    {sampleCompanies?.map((sample) => {
                      return (
                        <div key={sample} className={classes.samplesWrapper}>
                          <div className={classes.termText} style={{}}>
                            <span>{sample}</span>
                          </div>
                          <Button variant="contained" onClick={() => handleCopyClick(sample)}>
                            Copy text
                          </Button>
                          {isValidUrl(sample) ? (
                            <a target="_blank" rel="noreferrer" href={sample}>
                              Visit it
                            </a>
                          ) : (
                            <a target="_blank" rel="noreferrer" href={googleIt(sample)}>
                              Google it
                            </a>
                          )}
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <p>No sample companies defined</p>
                )
              }}
            />
          </div>
        </FormSection>
        <FormSection title="Criteria">
          <div className={classes.formContainer}>
            <FastField
              checked={values.includeCompaniesWithoutMaturityInformation}
              component={SwitchField}
              label="Receive companies without funding information"
              name="includeCompaniesWithoutMaturityInformation"
              type="checkbox"
            />
          </div>
          <div className={classes.formContainer}>
            <FastField
              component={CriteriaLocationSelect}
              className={classes.field}
              label="Headquarters location"
              name="organizationHeadquarters"
              locationFieldName="locations"
              isMulti={true}
            />
            <FastField
              component={CriteriaLocationSelect}
              className={classes.field}
              label="Negative headquarters locations"
              name="negativeOrganizationHeadquarters"
              locationFieldName="locations"
              isMulti={true}
            />
          </div>
          <div className={classes.formContainer}>
            <FastField
              component={StageSelect}
              className={classes.field}
              label="Last funding event"
              name="organizationStages"
              isMulti={true}
            />
          </div>
          <InputLabel shrink className={classes.label}>
            Companies founded between
          </InputLabel>
          <div className={classes.formContainer}>
            <FastField
              component={FoundedAtPicker}
              errors={errors}
              className={classes.field}
              label="Founded at"
              name="organizationFoundedAt"
              fullWidth
            />
          </div>
        </FormSection>
        <FormSection
          title="Relaxed Sweet Spot definition - Danger"
          subtitle="The following data can be displayed to final users in their Space"
          className={classes.dangerZone}
        >
          <FastField name="relaxedSweetSpotDefinition" component={HtmlEditorField} removeDefaultHtmlStyle />{' '}
        </FormSection>
        <FormSection
          title="Danger - Delete Action"
          subtitle="If you change the value of this toggle you may restore or remove this Space from User Dashboard"
          className={classes.dangerZone}
        >
          <FastField
            type="checkbox"
            component={SwitchField}
            label="Space is deleted"
            name="isDeleted"
            className={classes.isDeleted}
          />
        </FormSection>
        <FormSection title="Internal">
          <div>
            <FastField
              component={OpsLeadSelect}
              label="Assigned OPS Lead"
              name="assignedOpsLeadId"
              className={classes.field}
            />
          </div>
          <div className={classes.formContainer}>
            <FastField
              className={classes.field}
              component={NumberField}
              label="Daily companies limit"
              placeholder="2"
              name="dailyCompanyLimit"
              fullWidth
              helperText="Maximum number of companies discovered for this investor per workday (Mon - Fri). We don’t discover companies on weekends (Sat - Sun)."
              internalInputProps={{ decimalScale: 0 }}
              InputLabelProps={{ shrink: true }}
            />
            <HoursBetweenDeals className={classes.field}></HoursBetweenDeals>
          </div>
          <div className={classes.formContainer}>
            <FastField
              component={TextField}
              label="Positive primary keywords sets"
              name="internalPositivePrimaryKeywordsSets"
              className={classes.field}
              fullWidth
              multiline
            />
            <FastField
              component={TextField}
              label="Positive secondary keywords"
              name="internalPositiveSecondaryKeywords"
              className={classes.field}
              fullWidth
              multiline
            />
          </div>
          <div className={classes.formContainer}>
            <FastField
              component={TextField}
              label="Negative primary keywords"
              name="internalNegativePrimaryKeywords"
              className={classes.field}
              fullWidth
              multiline
            />
            <FastField
              component={TextField}
              label="Negative secondary keywords"
              name="internalNegativeSecondaryKeywords"
              className={classes.field}
              fullWidth
              multiline
            />
          </div>
          <div className={classes.formContainer}>
            <FastField
              component={TextField}
              label="Locations"
              name="internalLocations"
              className={classes.field}
              fullWidth
              InputLabelProps={{
                shrink: !!values.internalLocations,
              }}
              multiline
            />
            <FastField
              component={TextField}
              label="Negative locations"
              name="internalNegativeLocations"
              className={classes.field}
              fullWidth
              InputLabelProps={{
                shrink: !!values.internalNegativeLocations,
              }}
              multiline
            />
          </div>
          <div className={classes.formContainer}>
            <FastField
              component={TextField}
              label="Last funding event"
              name="internalStages"
              className={classes.field}
              fullWidth
              InputLabelProps={{
                shrink: !!values.internalStages,
              }}
              multiline
            />
          </div>
          <div className={classes.formContainer}>
            <FastField
              type="checkbox"
              component={SwitchField}
              label="Companies without funding included"
              name="internalIncludeCompaniesWithoutMaturityInformation"
              className={classNames(classes.inlineField)}
              fullWidth
            />
          </div>
          <div className={classes.doubleFieldWrapper}>
            <Field
              component={YearPicker}
              className={classes.field}
              fullWidth
              title="Companies without founding date in the Pool will be included in the results"
              label="Minimum founding year"
              name="internalMinimumYear"
              minDate={new Date(Date.UTC(MIN_YEAR, 1))}
              maxDate={
                internalMaximumYear ? new Date(Date.UTC(internalMaximumYear, 1)) : new Date(Date.UTC(MAX_YEAR, 1))
              }
              value={internalMinimumYear ? new Date(Date.UTC(internalMinimumYear, 1)) : null}
              helperText={errors.internalMinimumYear ?? undefined}
              error={!isEmpty(errors.internalMinimumYear) ?? false}
              disabled={isSubmitting}
              onChange={(dateValue: Date | null) => {
                const newInternalMinimumYear = calculateDateFieldValue(dateValue)
                setFieldValue('internalMinimumYear', newInternalMinimumYear, true)
              }}
            />
            <Field
              component={YearPicker}
              className={classes.field}
              fullWidth
              label="Maximum founding year"
              name="internalMaximumYear"
              title="Companies without founding date in the Pool will be included in the results"
              value={internalMaximumYear ? new Date(Date.UTC(internalMaximumYear, 1)) : null}
              minDate={
                internalMinimumYear ? new Date(Date.UTC(internalMinimumYear, 1)) : new Date(Date.UTC(MIN_YEAR, 1))
              }
              maxDate={new Date(Date.UTC(MAX_YEAR, 1))}
              helperText={errors.internalMaximumYear ?? undefined}
              error={!isEmpty(errors.internalMaximumYear) ?? false}
              disabled={isSubmitting}
              onChange={(dateValue: Date | null) => {
                const newInternalMaximumYear = calculateDateFieldValue(dateValue)
                setFieldValue('internalMaximumYear', newInternalMaximumYear, true)
              }}
            />
          </div>
        </FormSection>
      </>

      <Portal container={actionBarRef?.current}>
        <FormActionBar onSubmit={handleSubmit}>
          <Button variant="contained" disabled={values.id === TemporalEntityId.new} onClick={onUpdateCriteria}>
            Update criteria
          </Button>
          <div className={classes.generateSearchCandidatesContainer}>
            <Button
              variant="contained"
              className={classNames({
                [classes.buttonSuccess]: success,
              })}
              disabled={loading || dirty || values.id === TemporalEntityId.new}
              onClick={() => handleSearchCandidatesGeneration()}
            >
              Generate Search Candidates
            </Button>
            {loading && <CircularProgress size={24} className={classes.buttonProgress} />}
          </div>
          {values.id !== TemporalEntityId.new ? (
            <Link to={getScoutingCampaignSearchCandidatesViewPath(Number(values.id))}>
              <TextLink>See Search Candidates →</TextLink>
            </Link>
          ) : null}
        </FormActionBar>
      </Portal>
    </FormContainer>
  )
}
